/**
 * Права интерфейса.
 *
 * Право на действие над турниром считает бэкенд и присылает в
 * `tournament.capabilities` — фронт его только читает. Функции, которые
 * выводили право из статуса турнира (генерация сетки, заморозка, анонс,
 * ввод результатов), удалены в KAN-998: они не вызывались ни разу, а правило
 * в них уже разошлось с серверным. Второе место, где живёт то же правило, —
 * это баг, который ждёт своего часа.
 *
 * Роли (платформенные и в организации) проверяются через `platform-roles`.
 */

import type { Tournament } from "@/entities/tournament/model/types";

import {
  hasPlatformRole,
  isOrgStaffRole,
  isSiteAdmin as isSiteAdminProfile,
} from "../../../shared/lib/platform-roles";

export function canManageTournament(
  userId: string | undefined,
  userRole: string | undefined,
  tournament: Tournament | null | undefined,
): boolean {
  if (!userId || !tournament) return false;

  // KAN-1252: сервер шлёт canManageTournament по тому же правилу, что и его manage-проверки.
  const caps = tournament.capabilities;
  if (typeof caps?.canManageTournament === "boolean") return caps.canManageTournament;

  // Старый ответ без флага: доверяем «да» частных прав, их «нет» доступ не отбирает —
  // они гаснут на поздних стадиях.
  if (
    caps?.canEditTournament ||
    caps?.canManageSettings ||
    caps?.canManageRegistrations ||
    caps?.canManageBracket
  ) {
    return true;
  }

  // creatorId/created_by_id: легаси-имена поля вне текущего Tournament —
  // fallback на случай старых закэшированных ответов.
  const legacy = tournament as unknown as Record<string, unknown>;
  const createdById =
    tournament.createdById ??
    (legacy["creatorId"] as string | undefined) ??
    (legacy["created_by_id"] as string | undefined) ??
    null;

  const isCreator = Boolean(createdById && createdById === userId);
  const orgRole = tournament.organizer?.currentUserRole ?? null;
  // Org staff (Owner/Admin/Moderator) can manage the organization's tournaments.
  const isOrgStaff = isOrgStaffRole(orgRole);
  const isSiteAdmin = isSiteAdminProfile({ primaryPlatformRole: userRole });

  return isSiteAdmin || isCreator || isOrgStaff;
}

export function canEditTournamentSettings(
  tournament: Tournament | null | undefined,
): boolean {
  if (!tournament) return false;
  return Boolean(tournament.capabilities?.canEditTournament);
}

export function canDeleteTournament(
  tournament: Tournament | null | undefined,
): boolean {
  if (!tournament) return false;
  return Boolean(tournament.capabilities?.canDeleteTournament);
}

export function canSeeTestScrims(
  profile:
    | {
        isSiteAdmin?: boolean;
        isAdmin?: boolean;
        platformRoles?: string[] | null;
        primaryPlatformRole?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!profile) return false;
  if (profile.isSiteAdmin) return true;
  if (profile.isAdmin) return true;
  return isSiteAdminProfile(profile) || hasPlatformRole(profile, "Developer");
}
